import { useState } from "react";
import axiosInstance from "../core/axios";
import axios, { AxiosResponse } from "axios";

interface FileDownloaderProps {
  url: string;
  method?: "get" | "post" | "put" | "delete";
  data?: any;
  fileName?: string;
}

interface FileDownloadResult {
  success: boolean;
  errorMessage?: string;
}

const useFileDownloader = () => {
  const [error, setError] = useState<string | null>(null);

  const downloadFile = async ({
    url,
    method = "get",
    data,
    fileName = "download",
  }: FileDownloaderProps): Promise<FileDownloadResult> => {
    try {
      const response: AxiosResponse = await axiosInstance({
        method,
        url,
        data,
        responseType: "blob",
      });

      const contentDisposition = response.headers["content-disposition"];
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      const downloadedFileName =
        (matches && matches[1] ? matches[1].replace(/['"]/g, "") : fileName) ||
        "download";

      const blob = new Blob([response.data], { type: "application/octet-stream" });
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", downloadedFileName);
      document.body.appendChild(link);

      link.click();

      window.URL.revokeObjectURL(objectUrl);
      return { success: true };
    } catch (err: unknown) {
      let errorMessage = "Error downloading file";

      if (axios.isAxiosError(err) && err.response && err.response.data) {
        try {
          const blob = err.response.data as Blob;
          const text = await blob.text();

          try {
            const json = JSON.parse(text);
            if (
              json &&
              typeof json === "object" &&
              (json.message || json.error || json.title)
            ) {
              errorMessage =
                (json.message as string) ||
                (json.error as string) ||
                (json.title as string) ||
                errorMessage;
            } else if (typeof json === "string" && json.trim()) {
              errorMessage = json;
            }
          } catch {
            if (text && text.trim()) {
              errorMessage = text;
            }
          }
        } catch {
          // Ignore parsing issues and fall back to default message
        }
      }

      setError(errorMessage);
      return { success: false, errorMessage };
    }
  };

  return { downloadFile, error };
};

export default useFileDownloader;
